// react
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

// components
import { Editor } from '@/components/organisms/Editor/Editor';
import { FieldDef } from '@/components/organisms/EditorFields/fieldHelper';

// libs
import { Card, CardColor, Icon } from '@/components/atoms';
import Markdown from 'react-markdown';
import { Link } from 'react-router-dom';

/**
 * CareProfileForm displays a single form from a care profile.
 */
export const CareProfileForm = ({
  className,
  form,
  value,
  onChange,
  careRecipientName,
  isDisabled,
  isAiHelper,
}: CareProfileFormProps) => {
  // memos
  const formValue = useMemo(() => {
    return value;
  }, [value]);

  // callbacks
  const placeholderReplacer = useCallback(
    (input: string | undefined) => {
      return input?.replace(
        '{careRecipient}',
        careRecipientName || 'the care recipient',
      );
    },
    [careRecipientName],
  );

  return (
    <Wrapper className={className}>
      <Header>
        <Title>{placeholderReplacer(form?.title)}</Title>
        {form?.description && (
          <InfoCard color={'yellow'} isBordered={false} elevation={0}>
            <InfoCardDetails>
              <StyledIcon icon={'info-circle'} fontSize={18}></StyledIcon>
              <TextWrapper>
                <InfoCardContent>
                  <Markdown
                    components={{
                      a: ({ children, href }) => (
                        <StyledLink to={href || ''}>{children}</StyledLink>
                      ),
                    }}
                  >
                    {placeholderReplacer(form.description)}
                  </Markdown>
                </InfoCardContent>
              </TextWrapper>
            </InfoCardDetails>
          </InfoCard>
        )}
      </Header>
      {form && value && (
        <Editor
          fields={form.fields}
          isActionsDisabled={true}
          isFloatingActions={false}
          value={formValue}
          onChange={(val) => onChange?.({ ...value, ...val })}
          isDisabled={!isDisabled}
        />
      )}
      {isAiHelper && (
        <CardColor key='card-content' color='blue' isBordered={false}>
          <StyledLabel>
            <strong>Need Support?</strong>
            <div>
              Ask any question using our AI chatbot, or schedule a call with
              your care expert.
            </div>
          </StyledLabel>
        </CardColor>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  margin-top: ${(props) => props.theme.spacing[32]};
  padding-bottom: ${(props) => props.theme.spacing[32]};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[8]};
`;

const Title = styled.div`
  font: ${(props) => props.theme.typography.heading['3xl']};
  color: ${(props) => props.theme.color.neutral[90]};
`;

const StyledLabel = styled.label`
  font: ${(props) => props.theme.typography.body.sm.regular};
  color: ${(props) => props.theme.color.neutral[90]};
  display: flex;
  flex-direction: column;
`;

const InfoCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
  padding: ${(props) => props.theme.spacing[16]};
  font: ${(props) => props.theme.typography.body.md.regular};
`;

const InfoCardDetails = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[8]};
  padding-left: ${(props) => props.theme.spacing[8]};
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoCardContent = styled.div`
  color: ${(props) => props.theme.color.neutral[90]};
  font: ${(props) => props.theme.typography.body.md.regular};
`;

const StyledIcon = styled(Icon)`
  padding-top: ${(props) => props.theme.spacing[4]};
`;

const StyledLink = styled(Link)`
  font: inherit;
  text-decoration: underline;
  color: ${(props) => props.theme.color.primary[80]};
`;

export interface CareProfileFormItem<T> {
  id: string;
  title: string;
  description?: string;
  isComplete?: boolean;
  fields: { [key in keyof T]: FieldDef };
}

interface CareProfileFormProps {
  className?: string;
  form?: CareProfileFormItem<any> | undefined;
  value?: any;
  onChange?: (value: any) => void;
  careRecipientName?: string;
  isDisabled?: boolean;
  isAiHelper?: boolean;
}

CareProfileForm.displayName = 'CareProfileForm';

export default CareProfileForm;
